
import React, { useContext, useEffect, useState } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import {
	Button,
	Card,
	CardBody, CardFooter,
	CardHeader,
	Col,
	Container, FormGroup, Input,
	Row,
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AuctionsContext } from '../../../../contexts/AuctionsProvider'
import { SessionContext } from '../../../../contexts/SessionProvider'
import AuctionsService from '../../../../services/AuctionsService'
import SiteService from '../../../../services/SiteService'
import VehicleInspectionsService from '../../../../services/VehicleInspectionsService'
import { trigger } from '../../../../events/customEvents'
import Enumeration from '../../../../enumeration'
import MissingPricesModal from '../../../../components/Modals/MissingPricesModal'
import PageTitleContextMenu from '../../../../components/PageTitles/PageTitleContextMenu'
import AuctionsNavBar from '../../../../components/Navbars/AuctionsNavBar'

const AuctionVehicle = ({ embedded = false, onRequestClose, onSelectDetailTab }) =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const sessionContext = useContext(SessionContext)
	const [openMissingPricesModal, setOpenMissingPricesModal] = useState(false)
	const [priceValidationMessages, setPriceValidationMessages] = useState([])
	const [auctionVehicle, setAuctionVehicle] = useState(undefined)
	const [vehicleLocation, setVehicleLocation] = useState(undefined)
	const [renderState, setRenderState] = useState(Date.now())
	const navigate = useNavigate()
	
	useEffect(() =>
	{
		initializeComponent()
	}, [])
	
	const initializeComponent = () =>
	{
		retrieveAuctionVehicle()
		retrieveVehicleLocation()
	}
	
	const retrieveVehicleLocation = () =>
	{
		setIsBusy(true)
		VehicleInspectionsService
			.getVehicleLocation(auctionsContext.auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setVehicleLocation(result.data.actionResult)
				setRenderState(Date.now())
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveAuctionVehicle = () =>
	{
		setIsBusy(true)
		AuctionsService
			.getAuctionVehicle(auctionsContext.auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setAuctionVehicle((previousAuctionVehicle) =>
				{
					const currentAuctionVehicle = result.data.actionResult
					
					currentAuctionVehicle.marketPrice = (currentAuctionVehicle.marketPrice === null) ? undefined : currentAuctionVehicle.marketPrice
					currentAuctionVehicle.vehicle.warrantyStartDate = (currentAuctionVehicle.vehicle.warrantyStartDate === null) ? undefined : currentAuctionVehicle.vehicle.warrantyStartDate
					
					return currentAuctionVehicle
				})
				setRenderState(Date.now())
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleAuctionVehiclePropertyChange = (e) =>
	{
		const { name, value } = e.target
		setAuctionVehicle((previousAuctionVehicle) =>
		{
			return { ...previousAuctionVehicle, [name]: value }
		})
	}
	
	const handleVehiclePropertyChange = (e) =>
	{
		const { name, value} = e.target
		setAuctionVehicle((previousAuctionVehicle) =>
		{
			return { ...previousAuctionVehicle, vehicle: { ...previousAuctionVehicle.vehicle, [name]: value } }
		})
	}
	
	const handleSelectChange = (option) =>
	{
		handleVehiclePropertyChange(option)
	}
	
	const auctionVehiclePriceValidation = () =>
	{
		const validationResult =
			{
				validationMessages: [],
				isValid: true
			}
		
		let hasStartPrice = !isNaN(parseFloat(auctionVehicle.startPrice)) && auctionVehicle.startPrice > 0
		if (!hasStartPrice)
		{
			validationResult.validationMessages.push('The Start Price is invalid. It must be greater than zero.')
			validationResult.isValid = false
		}
		
		let hasReservePrice = !isNaN(parseFloat(auctionVehicle.reservePrice)) && auctionVehicle.reservePrice > 0
		if (!hasReservePrice)
		{
			validationResult.validationMessages.push('The Reserve Price is invalid. It must be greater than zero.')
			validationResult.isValid = false
		}
		
		let hasBuyNowPrice = true
		if (!isNaN(parseFloat(auctionVehicle.buyNowPrice)))
		{
			hasBuyNowPrice = auctionVehicle.buyNowPrice > 0
		}
		if (!hasBuyNowPrice)
		{
			validationResult.validationMessages.push('The Buy Now Price is invalid. If supplied it must be greater than zero.')
			validationResult.isValid = false
		}
		
		const hasMarketTier = auctionsContext.auction.auctionTiers.find((auctionTier) => auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.MBPTier)
		const hasOriginalDealerTier = auctionsContext.auction.auctionTiers.find((auctionTier) => auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.OriginalDealerTier)
		const hasMarketPrice = (hasMarketTier && (!isNaN(parseFloat(auctionVehicle.marketPrice)) && auctionVehicle.marketPrice > 0)) || !hasMarketTier
		if ((hasMarketTier || hasOriginalDealerTier) && !hasMarketPrice)
		{
			validationResult.validationMessages.push('An MBP Tier or Original Dealer Tier exists but there is not a valid market price.')
			validationResult.isValid = false
		}
		
		return validationResult
	}
	
	const handleUpdateAuctionVehicle = (e) =>
	{
		e.preventDefault()
		
		const vehiclePriceValidation = auctionVehiclePriceValidation()
		if (vehiclePriceValidation.isValid)
		{
			actuallyUpdateAuctionVehicle()
		}
		else
		{
			setPriceValidationMessages(vehiclePriceValidation.validationMessages)
			acknowledgeMissingPrices()
		}
	}
	
	const acknowledgeMissingPrices = () =>
	{
		setOpenMissingPricesModal(true)
	}
	
	const actuallyUpdateAuctionVehicle = () =>
	{
		setOpenMissingPricesModal(false)
		setIsBusy(true)
		AuctionsService
			.updateAuctionVehicle(auctionVehicle, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				trigger('raiseNotification', { title: 'Update Success', message: 'The auction vehicle was updated.', type: 'success', duration: 5 })
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleGoBack = () =>
	{
		if (embedded)
		{
			if (typeof onRequestClose === 'function') onRequestClose()
			return
		}
		navigate('/secure/auctions-listing')
	}
	
	return (
		<div
			style={{
				zIndex: 500
			}}
			key={ renderState }
		>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			{
				!embedded &&
				<PageTitleContextMenu
					heading={ t('Auctions.Vehicle.Title') }
					subheading={ auctionsContext.auction.auctionVehicle.vehicle.year + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.make + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.model + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.trim + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.serialNumber
					}
					icon="pe-7s-ticket icon-gradient bg-premium-dark"
					headerOptions={ { showBack: true, backCommand: () => { handleGoBack() }, showReload: false, reloadCommand: null } }
				/>
			}
			<Container className={ embedded ? "" : "mt--6" } fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader style={{padding: '0.5rem 0.75rem'}}>
								<AuctionsNavBar
									parentComponent={'auction-vehicle'}
									onSelect={ embedded ? onSelectDetailTab : undefined }
								/>
							</CardHeader>
							<CardBody>
								{
									auctionVehicle &&
									<>
										<Row>
											<Col md="3">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Detail.StartPrice') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														name="startPrice"
														value={ auctionVehicle.startPrice }
														onChange={ handleAuctionVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
											<Col md="3">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Detail.ReservePrice') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														name="reservePrice"
														value={ auctionVehicle.reservePrice }
														onChange={ handleAuctionVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
											<Col md="3">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Detail.BuyNowPrice') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														name="buyNowPrice"
														value={ auctionVehicle.buyNowPrice }
														onChange={ handleAuctionVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
											<Col md="3">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Detail.MarketPrice') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														name="marketPrice"
														value={ auctionVehicle.marketPrice }
														onChange={ handleAuctionVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
										</Row>
										<Row>
											<Col md="6">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Vin') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.serialNumber }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.StockNumber') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.stockNumber }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.WarrantyStartDate') }
													</label>
													<Input
														className="form-control-sm"
														type="date"
														value={ auctionVehicle.vehicle.warrantyStartDate }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.DaysInInventory') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.daysInInventory }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.AssetType') }
													</label>
													<Input
														className="form-control-sm"
														type="select"
														onChange={ handleSelectChange }
														value={ auctionVehicle.vehicle.assetTypeId }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.InventoryType') }
													</label>
													<Input
														className="form-control-sm"
														type="select"
														onChange={ handleSelectChange }
														value={ auctionVehicle.vehicle.inventoryTypeId }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Program') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.programId }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Transmission') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.transmission }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
											<Col md="6">
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Style') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.trim }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Year') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.year }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Make') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.make }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Model') }
													</label>
													<Input
														disabled
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.model }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Series') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.series }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Engine') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.engine }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.Drivetrain') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.drivetrain }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.ExteriorColor') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.exteriorColor }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
												<FormGroup>
													<label className="form-control-label" >
														{ t('Auctions.Vehicle.InteriorColor') }
													</label>
													<Input
														className="form-control-sm"
														type="text"
														value={ auctionVehicle.vehicle.interiorColor }
														onChange={ handleVehiclePropertyChange }
													/>
												</FormGroup>
											</Col>
										</Row>
										<Row>
											<Col md="12">
												<Button
													block
													color="primary"
													className="btn-block btn-default"
													onClick={ handleUpdateAuctionVehicle }
												>
													{ t('Common.Update') }
												</Button>
											</Col>
										</Row>
									</>
								}
							</CardBody>
							<CardFooter></CardFooter>
						</Card>
						
						<Card>
							<CardBody>
								<Row>
									<Col md="6">
										<FormGroup>
											<label className="form-control-label" >
												{ t('Auctions.Location.LocationName') }
											</label>
											<Input
												disabled
												className="form-control-sm"
												type="text"
												value={ (vehicleLocation) ? vehicleLocation.locationName : '' }
											/>
										</FormGroup>
										<FormGroup>
											<label className="form-control-label" >
												{ t('Auctions.Location.Address') }
											</label>
											<Input
												disabled
												className="form-control-sm"
												type="text"
												value={ (vehicleLocation) ? vehicleLocation.address1 : '' }
											/>
											<Input
												disabled
												className="form-control-sm"
												type="text"
												value={ (vehicleLocation) ? vehicleLocation.city + ', ' + vehicleLocation.state + ' ' + vehicleLocation.zipcode : '' }
											/>
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<label className="form-control-label" >
												{ t('Auctions.Location.ContactName') }
											</label>
											<Input
												disabled
												className="form-control-sm"
												type="text"
												value={ (vehicleLocation) ? vehicleLocation.contactName : '' }
											/>
										</FormGroup>
										<FormGroup>
											<label className="form-control-label" >
												{ t('Auctions.Location.PhoneNumber') }
											</label>
											<Input
												disabled
												className="form-control-sm"
												type="text"
												value={ (vehicleLocation) ? vehicleLocation.phoneNumber : '' }
											/>
										</FormGroup>
									</Col>
								</Row>
							</CardBody>
							<CardFooter></CardFooter>
						</Card>
					</div>
				</Row>
			</Container>
			
			<MissingPricesModal
				openMissingPricesModal={ openMissingPricesModal }
				setOpenMissingPricesModal={ setOpenMissingPricesModal }
				priceValidationMessages={ priceValidationMessages }
			/>
		</div>
	)
}

export default AuctionVehicle
